import {
  Assets,
  Container,
  ContainerChild,
  Graphics,
  Sprite,
  Text,
  Texture,
} from "pixi.js";

import type {
  IChatPlatform,
  IPinnedChatMessage,
  IStudioWidget,
  IStudioWidgetChat,
} from "../../../../screens/Dashboard/SubComponents/Studio/interfaces/sub-interfaces/Widgets.interface";
import { eventBusService } from "../../../../services/eventBus/eventBusService";
import type {
  ChatEmote,
  ChatMessage,
} from "../../../../services/eventBus/types";
import {
  getChatDesign,
  type ChatDesign,
} from "../../../../data/chatDesignsCatalog";
import { ChatDesignRenderer } from "./ChatDesignRenderer";
import { chatSimulation } from "@/src/services/chatSimulation";
import { chatLinesForDesign } from "../../../../data/chatDesignLines";
import { subscribeInstalledDesigns } from "../../../../entities/designs/installedRegistry";

// ──────────────────────────────────────────────────────────────────────────
// Texture cache (module-level, shared across all chat widget instances)
// ──────────────────────────────────────────────────────────────────────────

const TEXTURE_CACHE_CAP = 200;
const textureCache = new Map<string, Texture>();
// Tracks load order so we can evict the LRU when over the cap.
const textureCacheOrder: string[] = [];
// Dedup in-flight requests so we don't fetch the same URL twice.
const textureLoadInFlight = new Map<string, Promise<Texture | null>>();

const touchTextureLRU = (url: string) => {
  const idx = textureCacheOrder.indexOf(url);
  if (idx !== -1) textureCacheOrder.splice(idx, 1);
  textureCacheOrder.push(url);
};

const evictOldestIfNeeded = () => {
  while (textureCacheOrder.length > TEXTURE_CACHE_CAP) {
    const oldest = textureCacheOrder.shift();
    if (!oldest) break;
    const tex = textureCache.get(oldest);
    if (tex) {
      try {
        tex.destroy(true);
      } catch {
        // Pixi sometimes complains if the texture is still referenced; ignore.
      }
      textureCache.delete(oldest);
    }
  }
};

/**
 * Returns the texture from cache (sync), or triggers an async load and
 * resolves with `null` synchronously. The `onReady` callback fires once
 * the texture lands — that's where the renderer actually swaps the sprite.
 *
 * Dedups concurrent loads of the same URL.
 */
const getOrLoadTexture = (
  url: string,
  onReady: (tex: Texture) => void,
): Texture | null => {
  if (!url) return null;
  const cached = textureCache.get(url);
  if (cached) {
    touchTextureLRU(url);
    return cached;
  }
  let inflight = textureLoadInFlight.get(url);
  if (!inflight) {
    inflight = Assets.load(url)
      .then((tex: Texture) => {
        textureCache.set(url, tex);
        textureCacheOrder.push(url);
        evictOldestIfNeeded();
        textureLoadInFlight.delete(url);
        return tex;
      })
      .catch(() => {
        textureLoadInFlight.delete(url);
        return null;
      });
    textureLoadInFlight.set(url, inflight);
  }
  void inflight.then((tex) => {
    if (tex) onReady(tex);
  });
  return null;
};

// ──────────────────────────────────────────────────────────────────────────
// Body composition (mixed text + emote runs)
// ──────────────────────────────────────────────────────────────────────────

type BodyRun =
  | { kind: "text"; value: string }
  | { kind: "emote"; url: string; name: string };

/**
 * Splits a chat message body into a sequence of text runs and emote runs,
 * using the `[start, end]` positions on each emote. Positions are
 * **inclusive** ranges in the original `text` string (matching Twitch's
 * convention).
 *
 * Text gaps are further tokenized into space-separated words so the
 * caller can do word-wrap layout (one Text instance per word). Emotes
 * become single units that flow alongside the words.
 */
const tokenizeBody = (
  text: string,
  emotes: ChatEmote[] | undefined,
): BodyRun[] => {
  if (!text) return [];
  if (!emotes || emotes.length === 0) {
    return text
      .split(/(\s+)/)
      .filter((s) => s.length > 0)
      .map((value) => ({ kind: "text" as const, value }));
  }

  // Flatten emote position pairs into a sorted list of disjoint ranges,
  // each carrying the emote URL/name. Twitch ranges can overlap in pathological
  // cases — we keep the first one we see and drop later overlaps.
  const ranges: Array<{
    start: number;
    end: number;
    url: string;
    name: string;
  }> = [];
  for (const emote of emotes) {
    for (const [start, end] of emote.positions) {
      ranges.push({ start, end, url: emote.url, name: emote.name });
    }
  }
  ranges.sort((a, b) => a.start - b.start);

  const runs: BodyRun[] = [];
  let cursor = 0;
  for (const range of ranges) {
    if (range.start < cursor) continue; // overlap → skip
    if (range.start > cursor) {
      const gap = text.slice(cursor, range.start);
      for (const piece of gap.split(/(\s+)/)) {
        if (piece.length > 0) runs.push({ kind: "text", value: piece });
      }
    }
    runs.push({ kind: "emote", url: range.url, name: range.name });
    cursor = range.end + 1;
  }
  if (cursor < text.length) {
    const tail = text.slice(cursor);
    for (const piece of tail.split(/(\s+)/)) {
      if (piece.length > 0) runs.push({ kind: "text", value: piece });
    }
  }
  return runs;
};

// ──────────────────────────────────────────────────────────────────────────
// Renderer state
// ──────────────────────────────────────────────────────────────────────────

interface RowSlot {
  container: Container;
  /** Per-platform low-alpha background fill; toggled by `showPlatformBg`. */
  platformBg: Graphics;
  /** Background fill drawn under the row when `message.isHighlighted`. */
  highlightBg: Graphics;
  /** Thin platform-color stripe along the left edge (independent toggle from `dot`). */
  platformAccent: Graphics;
  /** Platform-color dot (left of author when `showPlatformBadge`). */
  dot: Graphics;
  /** Author avatar sprite (when `showAvatar && message.authorAvatar`). */
  avatarSprite: Sprite;
  /** Reply prefix text (when `message.replyTo`). */
  replyText: Text;
  /** Author name. */
  authorText: Text;
  /** Holds Text+Sprite runs for the body. Recreated per message. */
  bodyContainer: Container;
  /** Tracks which message this slot is currently displaying so we can skip
   *  redundant updates when the buffer hasn't actually advanced. */
  currentId: string | null;
  /** setTimeout handle for `fadeAfterSec` auto-fade; cleared on reassign. */
  fadeTimer: ReturnType<typeof setTimeout> | null;
  /** Animation interval handle for the active alpha tween, if any. */
  fadeTween: ReturnType<typeof setInterval> | null;
  /** Token bumped on every message reassignment so async loads (avatar /
   *  emote textures) can short-circuit when their target slot has moved on. */
  loadToken: number;
}

interface WidgetState {
  destination: { x: number; y: number; width: number; height: number };
  options: IStudioWidgetChat["options"];
  /** Lo que recibió `render`, para rehacerlo si cambia la resolución del diseño. */
  widget: IStudioWidget;
  container: Container;
  /** Teardown de la suscripción al registro de diseños instalados. */
  unsubscribeRegistry: () => void;
  /** Diseño resuelto en `render` (identidad estable por payload: ver `getChatDesign`). */
  design?: ChatDesign;
  /**
   * Set ONLY when `options.designId` resolves to a catalog design — the legacy
   * Pixi row pool below is then absent and the panel sprite is driven via
   * `designRenderer.update(messages)` from `repaint`.
   */
  designRenderer?: ChatDesignRenderer;
  bg?: Graphics;
  mask?: Graphics;
  rowsContainer?: Container;
  rows: RowSlot[];
  /** Local rolling buffer, already filtered. */
  messages: ChatMessage[];
  /** EventBus subscription teardown. */
  unsubscribe: () => void;
  /** Compiled regex filters. */
  blockedRegexes: RegExp[];
  /** Lowercased blocked usernames. */
  blockedUsers: Set<string>;
  /**
   * Slow-mode pending queue. When `options.slowModeMs > 0`, messages that
   * arrive within the cooldown window are buffered here and released by
   * `slowModeTimer` at the configured cadence. Capped at SLOW_MODE_QUEUE_CAP
   * — older entries dropped on overflow so a chat-storm doesn't leak memory.
   */
  slowModeQueue: ChatMessage[];
  /** Drain timer handle. Active iff slowModeQueue is non-empty. */
  slowModeTimer: ReturnType<typeof setTimeout> | null;
  /** Wall-clock of the last message that became visible (for slow-mode gate). */
  lastShownAt: number;
  /**
   * Reserved row pool for `options.pinnedMessages[]` — one slot per active
   * pin. Slots are lazily appended on first pin, kept across repaints, and
   * hidden (not destroyed) when un-pinned. Length grows up to
   * CHAT_PINNED_MAX over the widget's lifetime.
   */
  pinnedSlots: RowSlot[];
}

const PLATFORM_COLOR: Partial<Record<string, number>> = {
  twitch: 0x9146ff,
  youtube: 0xff0000,
  kick: 0x53fc18,
  restream: 0x1a73e8,
  facebook: 0x1877f2,
};

const FONT_SCALE = 2.01;
const HIGHLIGHT_COLOR = 0xffd700;
const HIGHLIGHT_ALPHA = 0.12;
const SLOW_MODE_QUEUE_CAP = 50;
/** Width in design px of the per-row platform accent stripe. */
const PLATFORM_ACCENT_WIDTH = 4;

const parseHex = (input: string | undefined, fallback: number): number => {
  if (!input) return fallback;
  const m = input.match(/^#?([0-9a-f]{6})$/i);
  return m ? parseInt(m[1], 16) : fallback;
};

const parseAlpha = (input: string | undefined): number => {
  if (!input) return 1;
  const rgba = input.match(
    /^rgba?\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*(?:,\s*([\d.]+))?\s*\)$/i,
  );
  if (rgba && rgba[1] !== undefined) return parseFloat(rgba[1]);
  return 1;
};

const fillFromColor = (input: string | undefined, fallback: number) => ({
  color: parseHex(input, fallback),
  alpha: parseAlpha(input),
});

const compileRegexes = (patterns: readonly string[] | undefined): RegExp[] => {
  if (!patterns || patterns.length === 0) return [];
  const out: RegExp[] = [];
  for (const raw of patterns) {
    try {
      out.push(new RegExp(raw, "i"));
    } catch {
      // Bad regex — UI will surface; ignore at runtime.
    }
  }
  return out;
};

const messagePassesFilters = (
  m: ChatMessage,
  platforms: Set<IChatPlatform> | null,
  blockedUsers: Set<string>,
  blockedRegexes: RegExp[],
): boolean => {
  if (platforms && !platforms.has(m.platform as IChatPlatform)) return false;
  if (m.authorName && blockedUsers.has(m.authorName.toLowerCase()))
    return false;
  for (const re of blockedRegexes) {
    if (re.test(m.text)) return false;
  }
  return true;
};

/** Truncate a quoted body so reply prefixes stay one-line. */
const truncateQuote = (text: string, max = 80): string => {
  if (!text) return "";
  const t = text.trim().replace(/\s+/g, " ");
  return t.length <= max ? t : `${t.slice(0, max - 1)}…`;
};

const clampMaxMessages = (n: number | undefined): number => {
  const v = n ?? 12;
  if (!Number.isFinite(v)) return 12;
  return Math.max(5, Math.min(30, Math.round(v)));
};

/**
 * Best-effort TTS via the browser Speech Synthesis API. Read aloud the
 * author + body of a highlighted chat message. We don't queue or block —
 * the API queues internally; if the OS rejects the speak (autoplay policy,
 * no voices installed, etc.) we silently skip.
 */
const speakHighlighted = (
  m: ChatMessage,
  options: IStudioWidgetChat["options"],
): void => {
  if (typeof window === "undefined") return;
  const synth = window.speechSynthesis;
  if (!synth) return;
  try {
    const utter = new SpeechSynthesisUtterance(`${m.authorName}: ${m.text}`);
    utter.rate = 1;
    utter.pitch = 1;
    if (options.ttsVoice) {
      const voice = synth.getVoices().find((v) => v.name === options.ttsVoice);
      if (voice) utter.voice = voice;
    }
    synth.speak(utter);
  } catch {
    // Some Electron / older Chromium builds throw on speak() before any
    // user gesture has happened — swallow.
  }
};

/**
 * Adapts a `IPinnedChatMessage` snapshot into the `ChatMessage` shape
 * the row renderer expects. Fields the snapshot doesn't carry get
 * cheap defaults — we only need what the row reads.
 */
const pinnedAsChatMessage = (p: IPinnedChatMessage): ChatMessage => ({
  id: p.id,
  platform: p.platform,
  authorId: p.id,
  authorName: p.authorName,
  authorAvatar: p.authorAvatar,
  authorBadges: [],
  authorColor: p.authorColor,
  text: p.text,
  emotes: p.emotes?.map((e) => ({
    id: e.name,
    name: e.name,
    url: e.url,
    positions: e.positions,
  })),
  timestamp: 0,
});

/**
 * Pixi renderer for the live Chat widget. Subscribes to the unified chat
 * stream via `eventBusService.onChat()` and draws the most recent N
 * messages into the broadcast canvas with full chat affordances:
 *
 * - Author name colored by platform (or `options.authorColor` override)
 * - Optional platform-color dot badge
 * - Optional avatar sprite (lazy texture load, LRU-cached)
 * - Inline emote sprites mixed with body text words (lazy texture load)
 * - Reply prefix `↩ @author` when the message has `replyTo`
 * - Highlighted-message background tint when `isHighlighted`
 * - Auto-fade after `fadeAfterSec` seconds (per message)
 *
 * Performance: rows are pooled (no per-message Container allocations);
 * body children are rebuilt on slot reassignment but not on every chat
 * tick when the slot is showing the same message. Texture cache is
 * module-level and shared across all chat widget instances.
 */
export class ChatWidgetRenderer {
  private widgets = new Map<string, WidgetState>();

  render(
    widget: IStudioWidget,
    container: Container,
    destination: { x: number; y: number; width: number; height: number },
  ): void {
    const w = widget as IStudioWidgetChat;
    const o = w.options;

    console.log(
      "[ChatWidget] render() id=",
      widget.id,
      "destination=",
      destination,
      "containerParent=",
      container.parent?.label ?? "<no-parent>",
      "options.backgroundColor=",
      o.backgroundColor,
      "options.maxMessages=",
      o.maxMessages,
      "options.platforms=",
      o.platforms,
    );

    // Tear down any prior state for the same widget id (re-renders are
    // common when bounds / options change via the property panel).
    this.cleanupWidget(widget.id);

    container.removeChildren();

    // ── Design-store path ──────────────────────────────────────────────────
    // When `options.designId` resolves to a catalog design, render its PANEL
    // SVG (with the live messages) as a single sprite and skip the legacy Pixi
    // row pool / mask. The SAME chat subscription + platform/blocked filtering +
    // slow-mode + buffer drive it via `repaint` → `designRenderer.update`.
    // Absent/unknown ⇒ fall through to the legacy renderer below, unchanged.
    const design = getChatDesign(o.designId);
    if (design) {
      const capD = clampMaxMessages(o.maxMessages);
      const platformsSetD =
        !o.platforms || o.platforms.length === 0
          ? null
          : new Set<IChatPlatform>(o.platforms);
      const blockedRegexesD = compileRegexes(o.blockedPatterns);
      const blockedUsersD = new Set<string>(
        (o.blockedUsers ?? []).map((u) => u.toLowerCase()),
      );
      const seedD = eventBusService
        .getChatBuffer()
        .filter((m) =>
          messagePassesFilters(
            m,
            platformsSetD,
            blockedUsersD,
            blockedRegexesD,
          ),
        )
        .slice(-capD);

      const designRenderer = new ChatDesignRenderer(design);
      designRenderer.render(container, destination);

      const designState: WidgetState = {
        destination,
        options: o,
        widget,
        container,
        unsubscribeRegistry: () => {},
        design,
        designRenderer,
        rows: [],
        messages: seedD,
        unsubscribe: () => {},
        blockedRegexes: blockedRegexesD,
        blockedUsers: blockedUsersD,
        slowModeQueue: [],
        slowModeTimer: null,
        lastShownAt: 0,
        pinnedSlots: [],
      };
      this.widgets.set(widget.id, designState);
      designState.unsubscribe = this.subscribe(widget.id);
      designState.unsubscribeRegistry = this.watchRegistry(widget.id);
      this.repaint(widget.id);
      return;
    }

    let bg: Graphics | undefined;
    if (o.backgroundColor && o.backgroundColor !== "transparent") {
      try {
        bg = new Graphics();
        const fill = fillFromColor(o.backgroundColor, 0x0a0014);
        console.log("[ChatWidget] bg fill computed:", fill, "rect:", {
          w: destination.width,
          h: destination.height,
        });
        bg.roundRect(0, 0, destination.width, destination.height, 12).fill({
          color: fill.color,
          alpha: fill.alpha,
        });
        container.addChild(bg);
      } catch (err) {
        console.error("[ChatWidget] bg creation failed:", err);
      }
    }

    let mask: Graphics;
    try {
      mask = new Graphics();
      mask.rect(0, 0, destination.width, destination.height).fill(0xffffff);
      container.addChild(mask);
      container.mask = mask;
      console.log(
        "[ChatWidget] mask applied:",
        { w: destination.width, h: destination.height },
        "containerHasMask=",
        Boolean(container.mask),
      );
    } catch (err) {
      console.error("[ChatWidget] mask creation failed:", err);
      // Fallback: empty graphics so the state object stays well-typed.
      mask = new Graphics();
    }

    const rowsContainer = new Container();
    container.addChild(rowsContainer);

    const cap = clampMaxMessages(o.maxMessages);
    console.log("[ChatWidget] creating rows: cap=", cap);
    const rows: RowSlot[] = [];
    try {
      for (let i = 0; i < cap; i += 1) {
        rows.push(this.createRow(rowsContainer, o));
      }
      console.log("[ChatWidget] rows created:", rows.length);
    } catch (err) {
      console.error("[ChatWidget] createRow THREW at index=", rows.length, err);
      throw err;
    }

    const platformsList = o.platforms;
    const platformsSet =
      !platformsList || platformsList.length === 0
        ? null
        : new Set<IChatPlatform>(platformsList);
    const blockedRegexes = compileRegexes(o.blockedPatterns);
    const blockedUsers = new Set<string>(
      (o.blockedUsers ?? []).map((u) => u.toLowerCase()),
    );

    const seed = eventBusService
      .getChatBuffer()
      .filter((m) =>
        messagePassesFilters(m, platformsSet, blockedUsers, blockedRegexes),
      )
      .slice(-cap);

    const state: WidgetState = {
      destination,
      options: o,
      widget,
      container,
      unsubscribeRegistry: () => {},
      design,
      bg,
      mask,
      rowsContainer,
      rows,
      messages: seed,
      unsubscribe: () => {},
      blockedRegexes,
      blockedUsers,
      slowModeQueue: [],
      slowModeTimer: null,
      lastShownAt: 0,
      pinnedSlots: [],
    };
    this.widgets.set(widget.id, state);

    state.unsubscribe = this.subscribe(widget.id);
    state.unsubscribeRegistry = this.watchRegistry(widget.id);

    this.repaint(widget.id);
  }

  /**
   * Subscribe a widget to the unified chat stream. Applies the per-widget
   * platform/blocked filters, fires TTS for highlighted messages, and honours
   * the slow-mode gate — identical for the legacy Pixi path and the design
   * (sprite) path, both of which funnel through `acceptMessage` → `repaint`.
   */
  private subscribe(widgetId: string): () => void {
    const unsubscribeLive = eventBusService.onChat((m) => {
      const s = this.widgets.get(widgetId);
      if (!s) return;
      const platformsSetLive =
        !s.options.platforms || s.options.platforms.length === 0
          ? null
          : new Set<IChatPlatform>(s.options.platforms);
      if (
        !messagePassesFilters(
          m,
          platformsSetLive,
          s.blockedUsers,
          s.blockedRegexes,
        )
      )
        return;

      // Fire-and-forget TTS for highlighted messages — independent of the
      // slow-mode gate so the audio cue lands immediately even if the
      // visual queue is paced.
      if (s.options.ttsHighlighted && m.isHighlighted) {
        speakHighlighted(m, s.options);
      }

      const slowModeMs = s.options.slowModeMs ?? 0;
      if (slowModeMs > 0) {
        const now = performance.now();
        if (now - s.lastShownAt < slowModeMs) {
          // Inside cooldown — queue it. Drop oldest if over cap.
          s.slowModeQueue.push(m);
          if (s.slowModeQueue.length > SLOW_MODE_QUEUE_CAP) {
            s.slowModeQueue.splice(
              0,
              s.slowModeQueue.length - SLOW_MODE_QUEUE_CAP,
            );
          }
          this.scheduleSlowModeDrain(widgetId);
          return;
        }
      }

      this.acceptMessage(widgetId, m);
    });
    const unsubscribeSimulation = chatSimulation.subscribe(widgetId, () =>
      this.repaint(widgetId),
    );
    return () => {
      unsubscribeLive();
      unsubscribeSimulation();
    };
  }

  /**
   * Re-resuelve `options.designId` cuando cambia el registro de diseños
   * instalados (hidratación async desde Dexie, instalar/quitar/re-guardar). Si
   * cambia el diseño resuelto — pasa a resolver, deja de hacerlo, o se
   * reemplazó el payload bajo el MISMO id de fila — se rehace el render
   * completo del widget: eso crea/destruye el `designRenderer` (o el pool
   * legacy) y repinta, con el mismo `container`/`destination` de `render`.
   */
  private watchRegistry(widgetId: string): () => void {
    return subscribeInstalledDesigns(() => {
      const s = this.widgets.get(widgetId);
      if (!s) return;
      // Identidad, no truthiness: un diseño re-guardado con el MISMO id de fila
      // es otro payload (el catálogo adapta cada payload una sola vez) y el
      // monitor debe rehacer el renderer para no quedarse con el viejo.
      if (getChatDesign(s.options.designId) === s.design) return;
      this.render(s.widget, s.container, s.destination);
    });
  }

  /**
   * Pushes a message into the visible buffer and triggers a repaint. This
   * is the path both direct messages (no slow-mode) and drained messages
   * (slow-mode) take — keeps the bookkeeping in one place.
   */
  private acceptMessage(widgetId: string, m: ChatMessage): void {
    const s = this.widgets.get(widgetId);
    if (!s) return;
    s.messages.push(m);
    const capLive = clampMaxMessages(s.options.maxMessages);
    if (s.messages.length > capLive) {
      s.messages.splice(0, s.messages.length - capLive);
    }
    s.lastShownAt = performance.now();
    this.repaint(widgetId);
  }

  /**
   * Schedules the slow-mode drain to fire `slowModeMs` after `lastShownAt`.
   * Idempotent: if a timer is already armed, this is a no-op.
   */
  private scheduleSlowModeDrain(widgetId: string): void {
    const s = this.widgets.get(widgetId);
    if (!s) return;
    if (s.slowModeTimer) return;
    const slowModeMs = s.options.slowModeMs ?? 0;
    if (slowModeMs <= 0) return;
    const now = performance.now();
    const elapsed = now - s.lastShownAt;
    const wait = Math.max(0, slowModeMs - elapsed);
    s.slowModeTimer = setTimeout(() => {
      const live = this.widgets.get(widgetId);
      if (!live) return;
      live.slowModeTimer = null;
      const next = live.slowModeQueue.shift();
      if (next) this.acceptMessage(widgetId, next);
      // Chain the next drain if still backed up.
      if (live.slowModeQueue.length > 0) {
        this.scheduleSlowModeDrain(widgetId);
      }
    }, wait);
  }

  private createRow(
    parent: Container,
    o: IStudioWidgetChat["options"],
  ): RowSlot {
    const fontFamily = o.fontFamily ?? "Inter, system-ui, sans-serif";
    const baseFontSize =
      (o.fontSize ? parseInt(o.fontSize, 10) : 16) * FONT_SCALE;
    const isCard = (o.layout ?? "card") === "card";
    const authorFontSize = isCard ? baseFontSize * 0.85 : baseFontSize;

    const rowContainer = new Container();
    rowContainer.visible = false;
    parent.addChild(rowContainer);

    // platformBg sits BELOW highlightBg in z-order so the highlight tint
    // (gold) wins on highlighted messages even when both are visible.
    const platformBg = new Graphics();
    platformBg.visible = false;
    rowContainer.addChild(platformBg);

    const highlightBg = new Graphics();
    highlightBg.visible = false;
    rowContainer.addChild(highlightBg);

    const platformAccent = new Graphics();
    platformAccent.visible = false;
    rowContainer.addChild(platformAccent);

    const avatarSprite = new Sprite();
    avatarSprite.visible = false;
    rowContainer.addChild(avatarSprite as unknown as ContainerChild);

    const dot = new Graphics();
    dot.circle(0, 0, baseFontSize * 0.18).fill(0xffffff);
    dot.visible = false;
    rowContainer.addChild(dot);

    const replyText = new Text({
      text: "",
      style: {
        fontFamily,
        fontSize: authorFontSize * 0.7,
        fill: 0xaaaaaa,
        fontStyle: "italic",
      },
    });
    replyText.visible = false;
    rowContainer.addChild(replyText as unknown as ContainerChild);

    const authorText = new Text({
      text: "",
      style: {
        fontFamily,
        fontSize: authorFontSize,
        fill: 0xffffff,
        fontWeight: "bold",
      },
    });
    rowContainer.addChild(authorText as unknown as ContainerChild);

    const bodyContainer = new Container();
    rowContainer.addChild(bodyContainer);

    return {
      container: rowContainer,
      platformBg,
      highlightBg,
      platformAccent,
      dot,
      avatarSprite,
      replyText,
      authorText,
      bodyContainer,
      currentId: null,
      fadeTimer: null,
      fadeTween: null,
      loadToken: 0,
    };
  }

  /**
   * Composes the body Container's children for a message — text words +
   * emote sprites — and lays them out left-to-right with line wrapping.
   * Returns the total composed height so the caller can stack rows.
   */
  private composeBody(
    slot: RowSlot,
    message: ChatMessage,
    o: IStudioWidgetChat["options"],
    wrapWidth: number,
  ): number {
    const { bodyContainer } = slot;

    // Tear down old body children. The body re-builds per-message; this
    // is the cost we pay for inline emote support (vs the trivial
    // `slot.bodyText.text = ...` of Phase 2).
    bodyContainer.removeChildren();

    const fontFamily = o.fontFamily ?? "Inter, system-ui, sans-serif";
    const baseFontSize =
      (o.fontSize ? parseInt(o.fontSize, 10) : 16) * FONT_SCALE;
    const lineHeight = baseFontSize * 1.3;
    const messageColor = parseHex(o.messageColor, 0xffffff);
    const wordGap = baseFontSize * 0.18;
    const emoteSize = baseFontSize * 1.1;

    const showEmotes = o.showEmotes ?? true;
    const runs = showEmotes
      ? tokenizeBody(message.text, message.emotes)
      : tokenizeBody(message.text, undefined);

    let cursorX = 0;
    let cursorY = 0;
    const slotToken = slot.loadToken;

    for (const run of runs) {
      let nodeWidth: number;
      let nodeHeight: number;
      let placedNode: ContainerChild;

      if (run.kind === "text") {
        if (/^\s+$/.test(run.value)) {
          // Pure whitespace tokens just advance the cursor a bit. Don't
          // create a Text instance for them.
          cursorX += wordGap;
          continue;
        }
        const t = new Text({
          text: run.value,
          style: {
            fontFamily,
            fontSize: baseFontSize,
            fill: messageColor,
          },
        });
        nodeWidth = t.width;
        nodeHeight = t.height;
        placedNode = t as unknown as ContainerChild;
      } else {
        const placeholder = new Sprite();
        placeholder.width = emoteSize;
        placeholder.height = emoteSize;

        const cached = getOrLoadTexture(run.url, (tex) => {
          // Async path — bail if this slot is now showing a different
          // message (the loadToken got bumped).
          if (slot.loadToken !== slotToken) return;
          if (placeholder.destroyed) return;
          placeholder.texture = tex;
          placeholder.width = emoteSize;
          placeholder.height = emoteSize;
        });
        if (cached) placeholder.texture = cached;
        else placeholder.texture = Texture.EMPTY;

        nodeWidth = emoteSize;
        nodeHeight = emoteSize;
        placedNode = placeholder as unknown as ContainerChild;
      }

      // Line wrap.
      if (cursorX > 0 && cursorX + nodeWidth > wrapWidth) {
        cursorX = 0;
        cursorY += lineHeight;
      }
      // Vertical center within the line.
      placedNode.x = cursorX;
      placedNode.y = cursorY + (lineHeight - nodeHeight) / 2;
      bodyContainer.addChild(placedNode);
      cursorX += nodeWidth + wordGap;
    }

    return cursorY + lineHeight;
  }

  /** Clears the in-flight fade timer + interval for a slot. */
  private cancelFade(slot: RowSlot): void {
    if (slot.fadeTimer) {
      clearTimeout(slot.fadeTimer);
      slot.fadeTimer = null;
    }
    if (slot.fadeTween) {
      clearInterval(slot.fadeTween);
      slot.fadeTween = null;
    }
  }

  /** Schedules an alpha fade-out N seconds after the slot lit up. */
  private scheduleFade(slot: RowSlot, fadeAfterSec: number): void {
    this.cancelFade(slot);
    if (!fadeAfterSec || fadeAfterSec <= 0) return;
    const slotToken = slot.loadToken;
    slot.fadeTimer = setTimeout(() => {
      if (slot.loadToken !== slotToken) return;
      slot.fadeTimer = null;
      const FADE_MS = 600;
      const STEP_MS = 32;
      const startA = slot.container.alpha;
      const startT = performance.now();
      slot.fadeTween = setInterval(() => {
        if (slot.loadToken !== slotToken) {
          this.cancelFade(slot);
          return;
        }
        const t = (performance.now() - startT) / FADE_MS;
        if (t >= 1) {
          slot.container.alpha = 0;
          slot.container.visible = false;
          this.cancelFade(slot);
          return;
        }
        slot.container.alpha = startA * (1 - t);
      }, STEP_MS);
    }, fadeAfterSec * 1000);
  }

  /**
   * Writes message content into a row slot's child nodes (text, avatar,
   * reply prefix, etc.) and measures the resulting row height. Idempotent
   * w.r.t. `slot.currentId` — re-applies cheaply when the slot's already
   * showing the same message.
   *
   * Used by both the rolling row pool and the (single) pinned slot.
   */
  private populateRow(
    slot: RowSlot,
    message: ChatMessage,
    ctx: {
      o: IStudioWidgetChat["options"];
      isCard: boolean;
      showBadge: boolean;
      showAccent: boolean;
      showAvatar: boolean;
      showPlatformBg: boolean;
      baseFontSize: number;
      lineGap: number;
      avatarSize: number;
      dotR: number;
      wordGap: number;
      wrapWidth: number;
    },
  ): { height: number; indent: number; isNewMessage: boolean } {
    const {
      o,
      isCard,
      showBadge,
      showAccent,
      showAvatar,
      showPlatformBg,
      lineGap,
      avatarSize,
      dotR,
      wordGap,
      wrapWidth,
    } = ctx;

    slot.container.visible = true;

    const isNewMessage = slot.currentId !== message.id;
    if (isNewMessage) {
      slot.currentId = message.id;
      slot.loadToken += 1;
      slot.container.alpha = 1;
    }

    const platformColor = PLATFORM_COLOR[message.platform] ?? 0xcccccc;
    const authorColor = o.authorColor
      ? parseHex(o.authorColor, platformColor)
      : message.authorColor
        ? parseHex(message.authorColor, platformColor)
        : platformColor;

    slot.dot.tint = platformColor;
    slot.dot.visible = showBadge;
    slot.platformAccent.tint = platformColor;
    slot.platformAccent.visible = showAccent;
    slot.platformBg.tint = platformColor;
    slot.platformBg.visible = showPlatformBg;

    const avatarUrl =
      showAvatar && message.authorAvatar ? message.authorAvatar : null;
    slot.avatarSprite.visible = !!avatarUrl;
    if (avatarUrl && isNewMessage) {
      const slotToken = slot.loadToken;
      const cached = getOrLoadTexture(avatarUrl, (tex) => {
        if (slot.loadToken !== slotToken) return;
        if (slot.avatarSprite.destroyed) return;
        slot.avatarSprite.texture = tex;
        slot.avatarSprite.width = avatarSize;
        slot.avatarSprite.height = avatarSize;
      });
      slot.avatarSprite.texture = cached ?? Texture.EMPTY;
      slot.avatarSprite.width = avatarSize;
      slot.avatarSprite.height = avatarSize;
    }

    const hasReply = !!message.replyTo;
    slot.replyText.visible = hasReply;
    if (hasReply && isNewMessage) {
      // Inline the quoted body when the upstream supplied it (truncated to
      // keep the reply prefix one-line). Falls back to author-only when
      // the field is missing.
      const reply = message.replyTo!;
      const quoted = reply.text ? truncateQuote(reply.text) : "";
      slot.replyText.text = quoted
        ? `↩ @${reply.authorName}: ${quoted}`
        : `↩ @${reply.authorName}`;
    }

    if (isNewMessage) {
      const authorLabel = isCard
        ? message.authorName
        : `${message.authorName}:`;
      slot.authorText.text = authorLabel;
    }
    slot.authorText.style.fill = authorColor;

    let bodyHeight = 0;
    if (isNewMessage) {
      const indentForBody = avatarUrl ? avatarSize + 8 : 0;
      const prefixWidth = isCard
        ? indentForBody
        : indentForBody +
          (showBadge ? dotR * 2 + 6 : 0) +
          slot.authorText.width +
          wordGap;
      const bodyWrapWidth = Math.max(60, wrapWidth - prefixWidth);
      bodyHeight = this.composeBody(slot, message, o, bodyWrapWidth);
    } else {
      bodyHeight = slot.bodyContainer.height;
    }

    slot.highlightBg.visible = !!message.isHighlighted;

    const authorH = slot.authorText.height;
    const replyH = hasReply ? slot.replyText.height : 0;
    const avatarH = avatarUrl ? avatarSize : 0;
    const headerH = Math.max(authorH, avatarH);
    const cardH =
      replyH +
      (replyH > 0 ? lineGap * 0.3 : 0) +
      headerH +
      lineGap * 0.5 +
      bodyHeight;
    const compactH = Math.max(headerH, bodyHeight, replyH);
    return {
      height: isCard ? cardH : compactH,
      indent: avatarUrl ? avatarSize + 8 : 0,
      isNewMessage,
    };
  }

  /**
   * Lays out a slot's CHILDREN (avatar, dot, reply, author, body, accent,
   * highlight) within the slot's local space. Caller positions the slot's
   * outer x/y. Pulled out so the pinned slot and rolling rows share the
   * exact same in-row layout math.
   */
  private layoutRowChildren(
    slot: RowSlot,
    h: number,
    indent: number,
    ctx: {
      isCard: boolean;
      lineGap: number;
      dotR: number;
      wordGap: number;
      destinationWidth: number;
      padX: number;
    },
  ): void {
    const { isCard, lineGap, dotR, wordGap, destinationWidth, padX } = ctx;

    if (slot.platformBg.visible) {
      // White-fill graphic that we tint per-platform via the sprite's
      // `tint`. Alpha at 0.10 keeps the row text readable on a busy
      // canvas. Bleeds to -padX so the tint reaches the panel edge.
      slot.platformBg
        .clear()
        .roundRect(-padX, -2, destinationWidth, h + 4, 6)
        .fill({ color: 0xffffff, alpha: 0.1 });
    }

    slot.highlightBg
      .clear()
      .roundRect(-padX, -2, destinationWidth, h + 4, 6)
      .fill({ color: HIGHLIGHT_COLOR, alpha: HIGHLIGHT_ALPHA });

    if (slot.platformAccent.visible) {
      slot.platformAccent
        .clear()
        .roundRect(-padX + 2, 0, PLATFORM_ACCENT_WIDTH, h, 2)
        .fill({ color: 0xffffff, alpha: 1 });
    }

    if (slot.avatarSprite.visible) {
      slot.avatarSprite.x = 0;
      slot.avatarSprite.y = 0;
    }

    if (isCard) {
      let yCursor = 0;
      if (slot.replyText.visible) {
        slot.replyText.x = indent;
        slot.replyText.y = yCursor;
        yCursor += slot.replyText.height + lineGap * 0.3;
      }

      if (slot.dot.visible) {
        slot.dot.x = indent + dotR;
        slot.dot.y = yCursor + slot.authorText.height / 2;
        slot.authorText.x = indent + dotR * 2 + 6;
      } else {
        slot.authorText.x = indent;
      }
      slot.authorText.y = yCursor;
      yCursor += slot.authorText.height + lineGap * 0.5;

      slot.bodyContainer.x = indent;
      slot.bodyContainer.y = yCursor;
    } else {
      const rowMidY = h / 2;
      if (slot.dot.visible) {
        slot.dot.x = indent + dotR;
        slot.dot.y = rowMidY;
      }
      const authorX = indent + (slot.dot.visible ? dotR * 2 + 6 : 0);
      slot.authorText.x = authorX;
      slot.authorText.y = rowMidY - slot.authorText.height / 2;

      if (slot.replyText.visible) {
        slot.replyText.x = authorX;
        slot.replyText.y = rowMidY - slot.replyText.height / 2;
        slot.authorText.x = authorX + slot.replyText.width + wordGap;
      }

      slot.bodyContainer.x =
        slot.authorText.x + slot.authorText.width + wordGap;
      slot.bodyContainer.y = rowMidY - slot.bodyContainer.height / 2;
    }
  }

  private repaint(widgetId: string): void {
    const s = this.widgets.get(widgetId);
    if (!s) return;

    const simulation = chatSimulation.getSnapshot(widgetId);
    const platforms = s.options.platforms?.length
      ? new Set<IChatPlatform>(s.options.platforms)
      : null;
    const messages = simulation
      ? simulation
          .filter((m) =>
            messagePassesFilters(m, platforms, s.blockedUsers, s.blockedRegexes),
          )
          .slice(-clampMaxMessages(s.options.maxMessages))
      : s.messages;

    // Design path: hand the current buffer to the panel sprite renderer (it
    // coalesces re-rasters) and skip the legacy Pixi row layout entirely.
    if (s.designRenderer) {
      // Paridad con el editor: los pines (snapshot en `options.pinnedMessages`)
      // van adelante del buffer filtrado, sin duplicados y dentro del
      // presupuesto de filas — la MISMA `chatLinesForDesign` que usa
      // `ChatWidget.tsx`, así editor y monitor muestran las mismas filas.
      const pins = (s.options.pinnedMessages ?? []).map(pinnedAsChatMessage);
      s.designRenderer.update(
        chatLinesForDesign(pins, messages, s.designRenderer.maxRows),
      );
      return;
    }

    const { rows, options: o, destination } = s;
    const rowsContainer = s.rowsContainer;
    if (!rowsContainer) return;
    const isCard = (o.layout ?? "card") === "card";
    const padX = 12;
    const padY = 8;
    const baseFontSize =
      (o.fontSize ? parseInt(o.fontSize, 10) : 16) * FONT_SCALE;
    const lineGap = isCard ? baseFontSize * 0.35 : baseFontSize * 0.18;
    const showBadge = o.showPlatformBadge ?? true;
    const showAccent = o.showPlatformAccent ?? false;
    const showPlatformBg = o.showPlatformBg ?? false;
    const showAvatar = o.showAvatar ?? true;
    const fadeAfterSec = o.fadeAfterSec ?? 0;
    const dotR = baseFontSize * 0.18;
    const avatarSize = baseFontSize * 1.4;
    const wordGap = baseFontSize * 0.18;
    const wrapWidth = Math.max(120, destination.width - padX * 2);

    const populateCtx = {
      o,
      isCard,
      showBadge,
      showAccent,
      showAvatar,
      showPlatformBg,
      baseFontSize,
      lineGap,
      avatarSize,
      dotR,
      wordGap,
      wrapWidth,
    };
    const layoutCtx = {
      isCard,
      lineGap,
      dotR,
      wordGap,
      destinationWidth: destination.width,
      padX,
    };

    // ── Pinned slots — laid out at the TOP of the box. Reserves their
    //   stacked height so the rolling rows stop short and don't overlap.
    //   The pool grows lazily (max CHAT_PINNED_MAX entries, since the form
    //   caps the array there); excess slots are hidden.
    const pinnedMessages = o.pinnedMessages ?? [];
    let pinnedReservedHeight = 0;
    if (pinnedMessages.length > 0) {
      // Grow the pool as needed.
      while (s.pinnedSlots.length < pinnedMessages.length) {
        s.pinnedSlots.push(this.createRow(rowsContainer, o));
      }
      let pinCursorY = padY;
      for (let i = 0; i < s.pinnedSlots.length; i += 1) {
        const slot = s.pinnedSlots[i];
        const pin = pinnedMessages[i];
        if (!pin) {
          slot.container.visible = false;
          slot.currentId = null;
          continue;
        }
        const pseudo = pinnedAsChatMessage(pin);
        // Pinned messages explicitly highlight (gold tint) regardless of
        // the source's `isHighlighted` — pin IS the highlight.
        pseudo.isHighlighted = true;
        const { height: pHeight, indent: pIndent } = this.populateRow(
          slot,
          pseudo,
          populateCtx,
        );
        // Pinned never auto-fades.
        this.cancelFade(slot);
        slot.container.alpha = 1;
        slot.container.x = padX;
        slot.container.y = pinCursorY;
        this.layoutRowChildren(slot, pHeight, pIndent, layoutCtx);
        pinCursorY += pHeight + lineGap;
      }
      pinnedReservedHeight = pinCursorY - padY;
    } else {
      // No pins active: hide any leftover slots from a prior pin state.
      for (const slot of s.pinnedSlots) {
        slot.container.visible = false;
        slot.currentId = null;
      }
    }

    const heights: number[] = [];
    const indents: number[] = [];

    // First pass: per-row content + height measurement (rolling pool).
    for (let i = 0; i < rows.length; i += 1) {
      const slot = rows[i];
      const messageIdx = messages.length - (rows.length - i);
      const message = messageIdx >= 0 ? messages[messageIdx] : undefined;

      if (!message) {
        slot.container.visible = false;
        slot.currentId = null;
        this.cancelFade(slot);
        slot.container.alpha = 1;
        heights[i] = 0;
        indents[i] = 0;
        continue;
      }

      const { height, indent, isNewMessage } = this.populateRow(
        slot,
        message,
        populateCtx,
      );
      heights[i] = height;
      indents[i] = indent;
      if (isNewMessage) this.scheduleFade(slot, fadeAfterSec);
    }

    // Second pass: position from bottom up. Rolling rows stop above the
    // pinned slot's reserved region (if any).
    let cursorY = destination.height - padY;
    const stopY = padY + pinnedReservedHeight;
    for (let i = rows.length - 1; i >= 0; i -= 1) {
      const slot = rows[i];
      if (!slot.container.visible) continue;

      const h = heights[i];
      cursorY -= h;
      slot.container.x = padX;
      slot.container.y = cursorY;
      this.layoutRowChildren(slot, h, indents[i], layoutCtx);

      cursorY -= lineGap;

      if (cursorY < stopY) {
        for (let j = i - 1; j >= 0; j -= 1) {
          rows[j].container.visible = false;
          rows[j].currentId = null;
          this.cancelFade(rows[j]);
        }
        break;
      }
    }
  }

  private cleanupWidget(widgetId: string): void {
    const existing = this.widgets.get(widgetId);
    if (!existing) return;
    try {
      existing.unsubscribe();
    } catch {
      // best-effort
    }
    try {
      existing.unsubscribeRegistry();
    } catch {
      // best-effort
    }
    if (existing.slowModeTimer) {
      clearTimeout(existing.slowModeTimer);
      existing.slowModeTimer = null;
    }
    if (existing.designRenderer) {
      existing.designRenderer.destroy();
      existing.designRenderer = undefined;
    }
    for (const slot of existing.rows) {
      this.cancelFade(slot);
    }
    for (const slot of existing.pinnedSlots) {
      this.cancelFade(slot);
    }
    existing.pinnedSlots = [];
    this.widgets.delete(widgetId);
  }

  destroy(): void {
    for (const id of [...this.widgets.keys()]) {
      this.cleanupWidget(id);
    }
  }
}

export default ChatWidgetRenderer;
